'use client';

import { Skeleton } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { useGetUserByIdQuery } from '@/features/users/api/usersApi';
import { Avatar, Divider, Drawer, SecondaryButton } from '@web/ui';
import { formatDate } from '@web/utils';

import styles from './UserProfileDrawer.module.scss';

import type { UserProfile } from '@/features/users/types';

type UserProfileDrawerProps = {
  open: boolean;
  userId: string | null;
  currentUserId?: string | null;
  currentUserEmail?: string | null;
  onClose: () => void;
  onExited?: () => void;
  onMessage?: (userId: string) => void;
};

function getDisplayName(user: UserProfile) {
  return (
    `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
    user.email ||
    'Unknown member'
  );
}

function isCurrentUser({
  user,
  currentUserId,
  currentUserEmail,
}: {
  user: UserProfile;
  currentUserId?: string | null;
  currentUserEmail?: string | null;
}) {
  return Boolean(
    (currentUserId && user.id === currentUserId) ||
      (currentUserEmail && user.email === currentUserEmail),
  );
}

function UserProfileDrawerLoading() {
  return (
    <div className={styles.userProfileDrawer}>
      <div className={styles.userProfileDrawer__identity}>
        <Skeleton variant="circular" width={72} height={72} />
        <div className={styles.userProfileDrawer__identityText}>
          <Skeleton variant="rounded" width={160} height={22} />
          <Skeleton variant="rounded" width={120} height={18} />
        </div>
      </div>
      <div className={styles.userProfileDrawer__section}>
        <Skeleton variant="rounded" width={150} height={18} />
        <Skeleton variant="rounded" height={58} />
      </div>
      <div className={styles.userProfileDrawer__section}>
        <Skeleton variant="rounded" width={110} height={18} />
        <Skeleton variant="rounded" height={58} />
        <Skeleton variant="rounded" height={58} />
      </div>
    </div>
  );
}

function UserProfileDrawerContent({
  user,
  currentUserId,
  currentUserEmail,
  onClose,
  onMessage,
}: {
  user: UserProfile;
  currentUserId?: string | null;
  currentUserEmail?: string | null;
  onClose: () => void;
  onMessage?: (userId: string) => void;
}) {
  const name = getDisplayName(user);
  const canMessage =
    Boolean(onMessage) &&
    user.is_active &&
    !isCurrentUser({ user, currentUserId, currentUserEmail });

  return (
    <div className={styles.userProfileDrawer}>
      <header className={styles.userProfileDrawer__header}>
        <div className={styles.userProfileDrawer__identity}>
          <Avatar
            name={name}
            src={user.avatar_url ?? user.avatar ?? undefined}
            size="3xLarge"
            showTooltip={false}
          />
          <div className={styles.userProfileDrawer__identityText}>
            <strong>{name}</strong>
            <span>Team member</span>
          </div>
        </div>
      </header>

      <section className={styles.userProfileDrawer__section}>
        <h3>Contact information</h3>
        <div className={styles.userProfileDrawer__detail}>
          <span>Email</span>
          <strong>{user.email}</strong>
        </div>
      </section>

      <section className={styles.userProfileDrawer__section}>
        <h3>Account details</h3>
        <div className={styles.userProfileDrawer__details}>
          <div className={styles.userProfileDrawer__detail}>
            <span>Status</span>
            <strong>{user.is_active ? 'Active' : 'Inactive'}</strong>
          </div>
          <div className={styles.userProfileDrawer__detail}>
            <span>Joined</span>
            <strong>{formatDate(user.date_joined, 'MMM D, YYYY')}</strong>
          </div>
        </div>
      </section>

      <div className={styles.userProfileDrawer__footerWrap}>
        <Divider />
        <footer className={styles.userProfileDrawer__footer}>
          <SecondaryButton ariaLabel="Close user profile" onClick={onClose}>
            Close
          </SecondaryButton>
          {canMessage ? (
            <SecondaryButton
              ariaLabel={`Message ${name}`}
              onClick={() => {
                onClose();
                onMessage?.(user.id);
              }}
            >
              Message
            </SecondaryButton>
          ) : null}
        </footer>
      </div>
    </div>
  );
}

export function UserProfileDrawer({
  open,
  userId,
  currentUserId,
  currentUserEmail,
  onClose,
  onExited,
  onMessage,
}: UserProfileDrawerProps) {
  const { data, isLoading } = useGetUserByIdQuery(
    open && userId ? userId : skipToken,
  );
  const responseUser = data?.success === true ? data.data : null;
  const user = responseUser?.id === userId ? responseUser : null;
  const shouldShowLoading = isLoading || (open && Boolean(userId) && !user);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      onExited={onExited}
      anchor="right"
      width={360}
    >
      {shouldShowLoading ? <UserProfileDrawerLoading /> : null}
      {!shouldShowLoading && user ? (
        <UserProfileDrawerContent
          user={user}
          currentUserId={currentUserId}
          currentUserEmail={currentUserEmail}
          onClose={onClose}
          onMessage={onMessage}
        />
      ) : null}
      {!shouldShowLoading && !user ? (
        <div className={styles.userProfileDrawer}>
          <p className={styles.userProfileDrawer__empty}>
            User profile unavailable.
          </p>
        </div>
      ) : null}
    </Drawer>
  );
}
